import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Box, Button, CircularProgress, IconButton, InputAdornment, Link, Stack, TextField, Typography } from '@mui/material';
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { ILogin, LoginApi } from '../../../api/LoginApi';
import { IUser, UserApi } from '../../../api/UserApi';
import { handleErrorMessage } from '../../../component/ErrorDialog';
import AppBarContext from '../../../context/AppBarContext';
import brandLockupStacked from '../../../asset/logo/lockup-stacked-color.svg';
import { setUserLocalStorage } from '../../../page/Login';
import { INTER, NAVY, SLATE, yellowButtonSx } from '../studentStyles';

// Minimalist mobile login rendered in place at any /mockup/student* URL while logged out.
// Same JWT flow as page/Login.tsx, but no redirect: StudentApp re-renders once
// setIsLoggedIn(true) fires (after localStorage is populated, so the settings gate is accurate).
export default function StudentLogin() {
    const history = useHistory();
    const location = useLocation();
    const { setErrorMessage, setIsLoggedIn } = React.useContext(AppBarContext);

    const [loginErrorMessage, setLoginErrorMessage] = React.useState('');
    const [showPassword, setShowPassword] = React.useState(false);
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setLoginErrorMessage('');
        const data = new FormData(event.currentTarget);
        const loginObject: ILogin = {
            email: String(data.get('email')),
            password: String(data.get('password')),
            rememberMe: false,
        };
        setIsSubmitting(true);
        LoginApi.login(loginObject)
            .then(response => {
                if (response.status < 300) {
                    localStorage.setItem('jwt', response.data.token);
                    UserApi.get()
                        .then(userResponse => {
                            const user: IUser = userResponse.data;
                            setUserLocalStorage(user);
                            setIsLoggedIn(true);
                        })
                        .catch(err => {
                            handleErrorMessage(err, setErrorMessage, history, location);
                        })
                        .finally(() => {
                            setIsSubmitting(false);
                        });
                } else {
                    setLoginErrorMessage('Invalid email or password');
                    setIsSubmitting(false);
                }
            })
            .catch(() => {
                setLoginErrorMessage('Invalid email or password');
                setIsSubmitting(false);
            });
    };

    return (
        <Box sx={{ width: '100%', maxWidth: 430, mx: 'auto', px: 3, pt: 10, pb: 6 }}>
            <Stack spacing={4}>

                <Box sx={{ textAlign: 'center' }}>
                    <Box component="img" src={brandLockupStacked} alt="VerbaSchola" sx={{ height: 96, width: 'auto', display: 'block', mx: 'auto' }} />
                    <Typography sx={{ fontFamily: INTER, fontSize: 15, color: SLATE, mt: 1.5 }}>
                        Learn Chinese, a little every day.
                    </Typography>
                </Box>

                <Box component="form" onSubmit={handleSubmit} noValidate>
                    <Stack spacing={2}>
                        <TextField
                            required
                            fullWidth
                            id="email"
                            name="email"
                            label="Email"
                            autoComplete="email"
                            autoFocus
                        />
                        <TextField
                            required
                            fullWidth
                            id="password"
                            name="password"
                            label="Password"
                            type={showPassword ? 'text' : 'password'}
                            autoComplete="current-password"
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="Toggle password visibility"
                                            onClick={() => setShowPassword(prev => !prev)}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        {loginErrorMessage && (
                            <Typography sx={{ fontFamily: INTER, fontSize: 14 }} color="error">
                                {loginErrorMessage}
                            </Typography>
                        )}

                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            disabled={isSubmitting}
                            sx={yellowButtonSx}
                        >
                            {isSubmitting ? <CircularProgress size={24} sx={{ color: NAVY }} /> : 'Log in'}
                        </Button>

                        <Link
                            component="button"
                            type="button"
                            onClick={() => history.push('/reset_password_email')}
                            sx={{ fontFamily: INTER, fontSize: 13, color: SLATE, alignSelf: 'center', textDecorationColor: SLATE }}
                        >
                            Forgot password?
                        </Link>
                    </Stack>
                </Box>

            </Stack>
        </Box>
    );
}
